import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import { isImageFile, normalizeImageForBook } from '../utils/imageHelpers';
import { bookTypeChipLabel } from '../utils/bookLabels';
import { Plant, Briefcase, Heart, Star, Lightbulb, Leaf, Image as PhImage, MusicNote, CookingPot, House, User } from '@phosphor-icons/react';

const MEMORY_TOPICS = [
  { id: 'childhood',  icon: <Plant size={20} weight="duotone" />, label: 'Childhood & Growing Up',  color: '#7aad8c',
    prompt: `You are conducting a recorded memory interview. The person being interviewed is sitting with a family member who is typing their answers for them.

CRITICAL RULES:
- You are interviewing THEM (the subject), not the family member typing
- Ask ONE short question at a time, directed at the subject (use "you" meaning the subject)
- Keep every question under 15 words
- After each answer, say something warm and brief (1 sentence), then ask the next question
- Never ask the family member about themselves
- Never get confused about who you are talking to
- Do NOT ask clarifying questions about the process — just ask about their life
- Do NOT use markdown formatting. No bold, no headers, no asterisks. Just plain warm text.

FOLLOW-UP STYLE:
- Go DEEPER into each answer before moving on — ask about people, places, senses, feelings
- Ask "who was there?" "what did that feel like?" "what happened next?" — not "what does that mean to you?"
- Never ask about objects or possessions that "remind them" of something
- Vary your question style: yes/no, either/or ("summer or winter?"), and open-ended
- If they give a short answer, make the next question MORE specific and easier, not broader
- After 6-8 good exchanges on this topic, gently offer to write up the story

TOPIC: CHILDHOOD & GROWING UP

Start immediately with: "Hello [name]! I'm so glad we're doing this together. Where did you grow up as a child?"` },

  { id: 'work',       icon: <Briefcase size={20} weight="duotone" />, label: 'Work & Career',           color: '#7a9ead',
    prompt: `You are conducting a recorded memory interview. The person being interviewed is sitting with a family member who is typing their answers for them.

CRITICAL RULES:
- You are interviewing THEM (the subject), not the family member typing
- Ask ONE short question at a time, directed at the subject (use "you" meaning the subject)
- Keep every question under 15 words
- After each answer, say something warm and brief (1 sentence), then ask the next question
- Never ask the family member about themselves
- Never get confused about who you are talking to
- Do NOT ask clarifying questions about the process — just ask about their life
- Do NOT use markdown formatting. No bold, no headers, no asterisks. Just plain warm text.

FOLLOW-UP STYLE:
- Go DEEPER into each answer before moving on — ask about people, places, senses, feelings
- Ask "who was there?" "what did that feel like?" "what happened next?" — not "what does that mean to you?"
- Never ask about objects or possessions that "remind them" of something
- Vary your question style: yes/no, either/or ("summer or winter?"), and open-ended
- If they give a short answer, make the next question MORE specific and easier, not broader
- After 6-8 good exchanges on this topic, gently offer to write up the story

TOPIC: WORK & CAREER

Start immediately with: "Hello [name]! What was your first job ever?"` },

  { id: 'family',     icon: <Heart size={20} weight="duotone" />, label: 'Marriage & Family',       color: '#ad7a9e',
    prompt: `You are conducting a recorded memory interview. The person being interviewed is sitting with a family member who is typing their answers for them.

CRITICAL RULES:
- You are interviewing THEM (the subject), not the family member typing
- Ask ONE short question at a time, directed at the subject (use "you" meaning the subject)
- Keep every question under 15 words
- After each answer, say something warm and brief (1 sentence), then ask the next question
- Never ask the family member about themselves
- Never get confused about who you are talking to
- Do NOT ask clarifying questions about the process — just ask about their life
- Do NOT use markdown formatting. No bold, no headers, no asterisks. Just plain warm text.

FOLLOW-UP STYLE:
- Go DEEPER into each answer before moving on — ask about people, places, senses, feelings
- Ask "who was there?" "what did that feel like?" "what happened next?" — not "what does that mean to you?"
- Never ask about objects or possessions that "remind them" of something
- Vary your question style: yes/no, either/or ("summer or winter?"), and open-ended
- If they give a short answer, make the next question MORE specific and easier, not broader
- After 6-8 good exchanges on this topic, gently offer to write up the story

TOPIC: MARRIAGE & FAMILY

Start immediately with: "Hello [name]! Tell me — how did you meet the love of your life?"` },

  { id: 'memories',   icon: <Star size={20} weight="duotone" />, label: 'Favorite Memories',        color: '#ada07a',
    prompt: `You are conducting a recorded memory interview. The person being interviewed is sitting with a family member who is typing their answers for them.

CRITICAL RULES:
- You are interviewing THEM (the subject), not the family member typing
- Ask ONE short question at a time, directed at the subject (use "you" meaning the subject)
- Keep every question under 15 words
- After each answer, say something warm and brief (1 sentence), then ask the next question
- Never ask the family member about themselves
- Never get confused about who you are talking to
- Do NOT ask clarifying questions about the process — just ask about their life
- Do NOT use markdown formatting. No bold, no headers, no asterisks. Just plain warm text.

FOLLOW-UP STYLE:
- Go DEEPER into each answer before moving on — ask about people, places, senses, feelings
- Ask "who was there?" "what did that feel like?" "what happened next?" — not "what does that mean to you?"
- Never ask about objects or possessions that "remind them" of something
- Vary your question style: yes/no, either/or ("summer or winter?"), and open-ended
- If they give a short answer, make the next question MORE specific and easier, not broader
- After 6-8 good exchanges on this topic, gently offer to write up the story

TOPIC: FAVORITE MEMORIES

Start immediately with: "Hello [name]! What's one moment in your life you still smile about?"` },

  { id: 'wisdom',     icon: <Lightbulb size={20} weight="duotone" />, label: 'Life Advice & Wisdom',     color: '#0f172a',
    prompt: `You are conducting a recorded memory interview. The person being interviewed is sitting with a family member who is typing their answers for them.

CRITICAL RULES:
- You are interviewing THEM (the subject), not the family member typing
- Ask ONE short question at a time, directed at the subject (use "you" meaning the subject)
- Keep every question under 15 words
- After each answer, say something warm and brief (1 sentence), then ask the next question
- Never ask the family member about themselves
- Never get confused about who you are talking to
- Do NOT ask clarifying questions about the process — just ask about their life
- Do NOT use markdown formatting. No bold, no headers, no asterisks. Just plain warm text.

FOLLOW-UP STYLE:
- Go DEEPER into each answer before moving on — ask about people, places, senses, feelings
- Ask "who was there?" "what did that feel like?" "what happened next?" — not "what does that mean to you?"
- Never ask about objects or possessions that "remind them" of something
- Vary your question style: yes/no, either/or ("summer or winter?"), and open-ended
- If they give a short answer, make the next question MORE specific and easier, not broader
- After 6-8 good exchanges on this topic, gently offer to write up the story

TOPIC: LIFE ADVICE & WISDOM

Start immediately with: "Hello [name]! What's the most important lesson your life has taught you?"` },

  { id: 'faith',      icon: <Leaf size={20} weight="duotone" />, label: 'Faith & Values',           color: '#8c9ead',
    prompt: `You are conducting a recorded memory interview. The person being interviewed is sitting with a family member who is typing their answers for them.

CRITICAL RULES:
- You are interviewing THEM (the subject), not the family member typing
- Ask ONE short question at a time, directed at the subject (use "you" meaning the subject)
- Keep every question under 15 words
- After each answer, say something warm and brief (1 sentence), then ask the next question
- Never ask the family member about themselves
- Never get confused about who you are talking to
- Do NOT ask clarifying questions about the process — just ask about their life
- Do NOT use markdown formatting. No bold, no headers, no asterisks. Just plain warm text.

FOLLOW-UP STYLE:
- Go DEEPER into each answer before moving on — ask about people, places, senses, feelings
- Ask "who was there?" "what did that feel like?" "what happened next?" — not "what does that mean to you?"
- Never ask about objects or possessions that "remind them" of something
- Vary your question style: yes/no, either/or ("summer or winter?"), and open-ended
- If they give a short answer, make the next question MORE specific and easier, not broader
- After 6-8 good exchanges on this topic, gently offer to write up the story

TOPIC: FAITH & VALUES

Start immediately with: "Hello [name]! What belief or value has guided you most through life?"` },
];

const DEMENTIA_BASE_PROMPT = `You are helping capture memories from someone with memory loss. A caregiver sits beside them and types what they say.

RULES (follow these EXACTLY):
- Talk TO the person BY NAME. Not the caregiver. Say "Mom, what was that like?" NOT "Do you remember what she said?"
- ONE question per response. ONE. Not two. Not "this or that?" Just ONE simple question.
- Your response = one warm sentence + one short question. NOTHING MORE. Total response under 25 words.
- Example good response: "Oh, cherry tobacco — what a smell. Mom, did your daddy smoke that pipe every evening?"
- Example BAD response: "That's such a tender image. Do you remember what they would talk about while watching those cars? Or was there a particular time of day they'd sit out there?" ← TWO QUESTIONS, TOO LONG, TALKS TO CAREGIVER
- Simple words only. Say "happy" not "meaningful." Say "house" not "residence."
- DISTANT PAST ONLY. Childhood, teens, young adult years. NEVER ask about recent events.
- Sensory questions work best: smells, sounds, tastes, textures, temperatures.
- If they can't answer → say "That's alright" → ask something TOTALLY different and EASIER.
- If they repeat themselves → respond warmly as if it's new → move on.
- NEVER correct them. NEVER say "you mentioned that." The feeling matters, not facts.
- Fragments are gold. One sentence from them = a win.
- NO markdown. NO bold. NO asterisks. NO headers. Plain text only.
- NO abstract questions like "what does that mean to you?" Ask CONCRETE things.
- ALWAYS use their name in your question. Every single time.
- WINDING DOWN: If responses become very short (one word) or confused, ask one final warm closing question, then end with: "That's a beautiful memory, [name]. Thank you for sharing that with me."
- HARD DAY: If the caregiver notes it's a hard day, use the simplest possible words, ask only about the most distant memories (early childhood), and keep your responses to one sentence only — no warm opener, just the question.`;

const DEMENTIA_TRIGGERS = [
  { id: 'photo', icon: <PhImage size={20} weight="duotone" />, label: 'Showing a Photo', desc: 'They\'re looking at an old picture',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: PHOTO. The caregiver is showing them an old photograph.\n- Start by asking who they see\n- If they recognize someone, ask one thing about that person\n- If they don't, ask about something visible (a dress, a car, a place)\n- Stay with whatever they notice\n\nStart with: "Oh, look at this. [name], who's in this picture?"` },
  { id: 'music', icon: <MusicNote size={20} weight="duotone" />, label: 'Humming a Song', desc: 'A song or melody triggered something',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: MUSIC. A song came up, or they're humming.\n- Music memories are the last to fade\n- Ask about the song, who sang it, where they heard it\n- Ask if they danced, what the radio sounded like at home\n- Stay sensory: sounds, rhythms, voices\n\nStart with: "[name], that's a nice song. Where did you first hear it?"` },
  { id: 'kitchen', icon: <CookingPot size={20} weight="duotone" />, label: 'Food & Cooking', desc: 'Something about a meal or recipe',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: FOOD. A meal, recipe, or cooking came up.\n- Food memories are deep and sensory\n- Ask about mama's cooking, holiday meals, favorite dish\n- Stay sensory: smells, tastes, warmth of a kitchen\n- Ask who sat where at the table\n\nStart with: "[name], what did mama's kitchen smell like?"` },
  { id: 'home', icon: <House size={20} weight="duotone" />, label: 'Talking About Home', desc: 'They mentioned a house or place',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: HOME/PLACE. A house, street, or neighborhood came up.\n- Ask about the house they grew up in\n- Was it big or small, what color, who lived there\n- Did they share a room, what was the yard like\n- Physical spaces anchor memory strongly\n\nStart with: "[name], was your house big or little?"` },
  { id: 'person', icon: <User size={20} weight="duotone" />, label: 'Talking About Someone', desc: 'They mentioned a person by name',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: PERSON. They mentioned someone by name — parent, sibling, friend, anyone.\n- The caregiver's first message will tell you who was mentioned\n- Use that person's name immediately in your first question\n- Ask what they looked like, what they smelled like, what they did together\n- A funny story, what their voice sounded like, what they wore\n- Keep it about ONE person at a time\n- People are the strongest memory anchors — stay with this person as long as they will\n\nStart with: "[name], tell me about [person's name]. What did [he/she/they] look like?"\n\nNote to AI: Get the person's name from the caregiver's first message.\nIf no name was given, ask "[name], who are you thinking about right now?"` },
  { id: 'faith', icon: '⛪', label: 'Church or Faith', desc: 'They mentioned God, church, or prayer',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: FAITH. God, church, prayer, or something spiritual came up.\n- Ask about Sunday mornings, their church building\n- A favorite hymn, who they sat with\n- Keep it warm and personal, not theological\n\nStart with: "[name], did you go to church as a child?"` },
  { id: 'spark', icon: '', label: 'They Just Said Something', desc: 'Follow whatever they\'re talking about',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: FREE FORM. The person just said something and the caregiver wants to follow it.\n- The caregiver's first message describes what was just said\n- Mirror their exact words back warmly, then ask ONE concrete follow-up\n- If they mentioned a place, ask what it looked like\n- If they mentioned a person, ask one thing about that person\n- If they mentioned a feeling, ask about a specific moment that caused it\n- Never say "tell me more" — always ask something specific\n- Follow THEIR lead completely, stay with whatever thread they opened\n\nStart with: "[name], you said [repeat their exact words back]. [One concrete sensory question about it]?"\n\nNote to AI: Replace the bracketed parts with what the caregiver just described.\nIf the caregiver's message is unclear, ask "[name], say that again for me?"` },
  { id: 'childhood', icon: '🧒', label: 'Childhood Memories', desc: 'Early childhood years',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: CHILDHOOD. Exploring early childhood memories.\n- Ask about the house they grew up in, their neighborhood, games they played\n- Siblings, friends, pets, school\n- Stay sensory: smells, sounds, textures of childhood\n- Distant past only — before age 12\n\nStart with: "[name], what did your house look like when you were little?"` },
  { id: 'parents', icon: '👨‍👩‍', label: 'Their Parents', desc: 'Memories of mother and father',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: PARENTS. Memories of their mother or father.\n- Ask about what their parents looked like, what they did for work\n- A memory of something a parent did or said\n- Smells, sounds, feelings associated with their parents\n- Keep it warm and specific\n\nStart with: "[name], tell me about your mama. What do you remember most about her?"` },
  { id: 'marriage', icon: '💍', label: 'Marriage & Partnership', desc: 'Meeting their spouse or partner',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: MARRIAGE. Meeting and marrying their spouse or partner.\n- Ask where they met, what they looked like, first impressions\n- The wedding, early years together\n- Stay concrete and sensory\n\nStart with: "[name], where did you first meet your husband/wife?"` },
  { id: 'children', icon: '👶', label: 'Their Children', desc: 'Raising children and family life',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: CHILDREN. Memories of raising their children.\n- Ask about when the children were small, a specific memory\n- What a child was like, something funny or tender\n- Stay in the distant past — when children were young\n\nStart with: "[name], what do you remember about when your children were little?"` },
  { id: 'proudest_moment', icon: '🏆', label: 'A Proud Moment', desc: 'Something they are proud of',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: PROUD MOMENT. Something they accomplished or are proud of.\n- Ask about work they did, something they made or built\n- A moment someone recognized them, thanked them, or they felt proud\n- Keep it specific and concrete\n\nStart with: "[name], what's something you made or did that you felt really proud of?"` },
  { id: 'first_job', icon: '🔧', label: 'Their First Job', desc: 'Early work and career memories',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: FIRST JOB. Memories of early work and earning a living.\n- Ask about their first job, what they did, who they worked with\n- What the workplace smelled like, what they wore, what they earned\n- A coworker, a boss, something funny or hard that happened\n- Work memories are strong anchors — people remember their hands being busy\n\nStart with: "[name], what was the first job you ever had?"` },
  { id: 'hardship', icon: '🌧️', label: 'Getting Through Hard Times', desc: 'A difficulty they overcame',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: HARDSHIP. A difficult time they got through.\n- Approach gently — follow their lead entirely\n- If they go somewhere painful, stay with it warmly but briefly\n- Focus on how they got through it, who helped them, what they learned\n- Strength and resilience are the thread — not the pain itself\n- If they seem distressed, redirect: "That took real strength. [name], who was beside you through that?"\n\nStart with: "[name], what's the hardest thing you ever got through?"` },
  { id: 'what_they_want_remembered', icon: '💫', label: 'What They Want Remembered', desc: 'Their legacy and what matters most',
    prompt: DEMENTIA_BASE_PROMPT + `\n\nTRIGGER: LEGACY. What they want people to remember about them.\n- This is the most important session — take it slowly\n- Ask about what they're most proud of, what they stood for\n- What they want their grandchildren to know\n- What they believed in, what they lived by\n- Keep questions simple and concrete — not abstract\n- If they struggle, ask about a specific person: "What do you hope [grandchild's name] remembers about you?"\n\nStart with: "[name], what do you want people to remember about you when you're gone?"` },
];

// Map AI suggestion themes to DEMENTIA_TRIGGERS ids
const THEME_TO_TRIGGER = {
  childhood: 'childhood',
  parents: 'parents',
  siblings: 'person',
  school: 'childhood',
  first_job: 'first_job',
  marriage: 'marriage',
  children: 'children',
  home: 'home',
  faith: 'faith',
  hardship: 'hardship',
  proudest_moment: 'proudest_moment',
  what_they_want_remembered: 'what_they_want_remembered',
};

// Map AI suggestion theme to a MEMORY_TOPICS/DEMENTIA_TRIGGERS entry
const themeToTopic = (theme, isDementia) => {
  const themeMap = {
    childhood: 'childhood', parents: 'childhood', siblings: 'childhood',
    school: 'childhood', first_job: 'work', marriage: 'family',
    children: 'family', home: 'memories', faith: 'faith',
    hardship: 'wisdom', proudest_moment: 'memories', what_they_want_remembered: 'wisdom',
  };
  const topicId = themeMap[theme] || 'memories';
  if (isDementia) {
    const triggerId = THEME_TO_TRIGGER[theme] || 'spark';
    return DEMENTIA_TRIGGERS.find(t => t.id === triggerId) || DEMENTIA_TRIGGERS.find(t => t.id === 'spark') || DEMENTIA_TRIGGERS[0];
  }
  return MEMORY_TOPICS.find(t => t.id === topicId) || MEMORY_TOPICS[0];
};

// Relative date helper
const relativeDate = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
  return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
};

// Progress dots component
const ProgressDots = ({ mcStep }) => {
  const steps = ['welcome', 'mood', 'suggest', 'breath'];
  const currentIdx = steps.indexOf(mcStep);
  if (currentIdx < 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 24 }}>
      {steps.map((s, i) => {
        const isDone = i < currentIdx;
        const isActive = i === currentIdx;
        return (
          <div key={s} style={{
            width: isActive ? 24 : 8, height: 8, borderRadius: 20,
            background: isDone ? '#7aad8c' : isActive ? '#9b8faa' : 'transparent',
            border: isDone || isActive ? 'none' : '1.5px solid rgba(15,23,42,0.15)',
            transition: 'all 0.3s ease',
          }} />
        );
      })}
    </div>
  );
};

// Moment items for step 3
const MOMENT_ITEMS = [
  { icon: <PhImage size={20} weight="duotone" />, label: 'They\'re looking at a photo', triggerId: 'photo' },
  { icon: <MusicNote size={20} weight="duotone" />, label: 'A song came on', triggerId: 'music' },
  { icon: '', label: 'They just said something', triggerId: 'spark' },
  { icon: '🍲', label: 'Food or cooking came up', triggerId: 'kitchen' },
  { icon: '🏡', label: 'Talking about a place', triggerId: 'home' },
  { icon: '🙏', label: 'Church or faith', triggerId: 'faith' },
];

const MemoryInterviewPage = () => {
  const {
    // Navigation
    setActiveTab, setStBookStep, showToast,
    // Stories & journal
    stories, journalEntries, setViewingStory,
    // Memory Care subjects
    memoryCareSubjects, saveMemoryCareSubject,
    // Account settings (for name persistence)
    accountSettings, saveAccountSettings,
    // Memory Care guided flow
    mcStep, setMcStep, mcSubject, setMcSubject,
    mcMood, setMcMood, mcSessionTopic, setMcSessionTopic,
    mcSuggestion, mcSuggestionLoading, fetchSessionSuggestion,
    mcTargetBookId, setMcTargetBookId,
    mcShaping, handleShapeIntoChapter, mcOriginalTranscript, handleRestoreTranscript,
    // Books
    books,
    // Memory interview state
    memoryStep, setMemoryStep, memoryTab, setMemoryTab,
    memoryDementiaMode, setMemoryDementiaMode,
    setMemoryDayQuality,
    memoryTopic, setMemoryTopic,
    memorySubjectName, setMemorySubjectName,
    setMemoryCarerName,
    startMemoryInterview,
    memoryMessages, memoryLoading,
    memoryInput, setMemoryInput,
    sendMemoryMessage, generateMemoryStory, resetMemoryInterview,
    memoryTitle, setMemoryTitle,
    memoryStory, setMemoryStory,
    memoryPhotoPreview, setMemoryPhotoPreview,
    setMemoryPhotoFile,
    saveMemoryInterview, savingMemory,
    // Audio/Video recording
    interviewAudioUrl, interviewAudioBlob,
    setInterviewAudioBlob, setInterviewAudioUrl,
    interviewRecording, startInterviewRecording, stopInterviewRecording,
    interviewVideoMode, setInterviewVideoMode,
    interviewVideoBlob, setInterviewVideoBlob, interviewVideoUrl, setInterviewVideoUrl,
    interviewLiveVideoRef,
    transcribing, transcribeAudio,
  } = useAppContext();

  // ── Unsaved recording warning ──
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (interviewAudioBlob || interviewVideoBlob) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [interviewAudioBlob, interviewVideoBlob]);

  // Local state for the welcome form inputs — persist via accountSettings
  const [wcName, setWcName] = React.useState(() => {
    if (mcSubject) return mcSubject.name || '';
    if (memoryCareSubjects.length > 0) {
      const lastUsed = [...memoryCareSubjects].sort((a, b) =>
        (b.lastSessionDate || b.createdAt || '').localeCompare(a.lastSessionDate || a.createdAt || '')
      )[0];
      return lastUsed.name || '';
    }
    return accountSettings?.mcSubjectName || '';
  });
  const [wcCarerName, setWcCarerName] = React.useState(() => {
    if (mcSubject) return mcSubject.caregiverName || '';
    if (memoryCareSubjects.length > 0) {
      const lastUsed = [...memoryCareSubjects].sort((a, b) =>
        (b.lastSessionDate || b.createdAt || '').localeCompare(a.lastSessionDate || a.createdAt || '')
      )[0];
      return lastUsed.caregiverName || '';
    }
    return accountSettings?.mcCarerName || '';
  });
  const [selectedMoment, setSelectedMoment] = React.useState(null);
  const chatBottomRef = React.useRef(null);

  // Sync names when accountSettings loads async (after initial render)
  const didSyncNames = React.useRef(false);
  React.useEffect(() => {
    if (!didSyncNames.current && accountSettings?.mcSubjectName && !wcName) {
      setWcName(accountSettings.mcSubjectName);
      if (accountSettings.mcCarerName) setWcCarerName(accountSettings.mcCarerName);
      didSyncNames.current = true;
    }
  }, [accountSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll chat when new messages arrive
  React.useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [memoryMessages]);

  // Auto-select target book when entering review step
  React.useEffect(() => {
    if (memoryStep === 'review' && !mcTargetBookId && books.length > 0) {
      const personal = books.find(b => b.bookType === 'PERSONAL');
      setMcTargetBookId(personal ? personal.id : books[0].id);
    }
  }, [memoryStep, books, mcTargetBookId, setMcTargetBookId]);

  // Card wrapper style
  // Derive display subject for welcome step — either the selected mcSubject or the most recent one
  const displaySubject = mcSubject || (memoryCareSubjects.length > 0
    ? [...memoryCareSubjects].sort((a, b) =>
        (b.lastSessionDate || b.createdAt || '').localeCompare(a.lastSessionDate || a.createdAt || '')
      )[0]
    : null);

  const cardStyle = {
    maxWidth: 640, margin: '0 auto', background: '#fff',
    border: '1px solid rgba(15,23,42,0.08)', borderRadius: 20,
    padding: '36px 32px',
  };
  const cardStyleMobile = window.innerWidth < 480 ? { padding: '24px 20px' } : {};

  const eyebrowStyle = {
    fontSize: '0.82rem', letterSpacing: 3, textTransform: 'uppercase',
    color: '#9b8faa', marginBottom: 8, fontFamily: "'Lora', serif",
  };

  const headingStyle = {
    fontFamily: "'Lora', serif", fontSize: '1.35rem', fontWeight: 600,
    color: '#0f172a', marginBottom: 8, lineHeight: 1.4,
  };

  const subStyle = {
    fontSize: '0.92rem', color: '#3c4653', lineHeight: 1.6, marginBottom: 24,
  };

  const backLinkStyle = {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.9rem', color: '#3c4653', fontFamily: "'Lora', serif",
    padding: 0, marginTop: 16,
  };

  const primaryBtnStyle = {
    width: '100%', padding: '15px 24px', borderRadius: 14,
    border: 'none', cursor: 'pointer', fontSize: '1.05rem',
    fontFamily: "'Lora', serif", fontWeight: 600, transition: 'all 0.2s',
  };

  const lavenderBtnStyle = {
    ...primaryBtnStyle,
    background: 'linear-gradient(135deg, #9b8faa 0%, #8578a0 100%)',
    color: '#fff',
  };

  const goldBtnStyle = {
    ...primaryBtnStyle,
    background: 'linear-gradient(135deg, #7c2d12 0%, #b8963a 100%)',
    color: '#fff',
  };

  // Handle "Continue" on welcome step
  const handleWelcomeContinue = () => {
    const name = wcName.trim();
    if (!name) return;
    // Find existing subject or create new
    let subject = memoryCareSubjects.find(s => s.name === name);
    if (subject) {
      // Update caregiver name if changed
      if (wcCarerName.trim() !== (subject.caregiverName || '')) {
        subject = { ...subject, caregiverName: wcCarerName.trim() };
        saveMemoryCareSubject(subject);
      }
    } else {
      subject = {
        id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36),
        name,
        caregiverName: wcCarerName.trim(),
        createdAt: new Date().toISOString(),
        lastSessionDate: '',
        lastSessionTitle: '',
        sessionCount: 0,
        capturedThemes: [],
        mentions: [],
      };
      saveMemoryCareSubject(subject);
    }
    setMcSubject(subject);
    // Persist names to accountSettings so they survive page reloads
    saveAccountSettings({ mcSubjectName: name, mcCarerName: wcCarerName.trim() });
    setMcStep('mood');
  };

  // Handle "This is someone new"
  const handleNewSubject = () => {
    setMcSubject(null);
    setWcName('');
    setWcCarerName('');
  };

  // Handle mood select + advance
  const handleMoodContinue = () => {
    if (!mcMood) return;
    setMcStep('suggest');
    // Fire and forget — loads in background
    if (mcSubject) fetchSessionSuggestion(mcSubject, mcMood);
  };

  // Track pending session start — useEffect fires startMemoryInterview after state settles
  const [pendingSessionStart, setPendingSessionStart] = React.useState(false);

  // Handle begin session from breath step
  const handleBeginSession = () => {
    if (!mcSubject || !mcSessionTopic) return;
    // Set existing state that startMemoryInterview expects
    setMemorySubjectName(mcSubject.name);
    setMemoryCarerName(mcSubject.caregiverName || '');
    setMemoryDayQuality(mcMood === 'mixed' ? 'okay' : mcMood);
    // Map the session topic to a proper topic object
    const topic = mcSessionTopic.triggerId
      ? (DEMENTIA_TRIGGERS.find(t => t.id === mcSessionTopic.triggerId) || DEMENTIA_TRIGGERS[0])
      : mcSessionTopic.theme === 'free'
        ? (memoryDementiaMode
            ? DEMENTIA_TRIGGERS.find(t => t.id === 'spark') || DEMENTIA_TRIGGERS[0]
            : MEMORY_TOPICS[3])
        : themeToTopic(mcSessionTopic.theme, memoryDementiaMode);
    setMemoryTopic(topic);
    setMcStep('session');
    setPendingSessionStart(true);
  };

  // Fire startMemoryInterview after state has flushed
  React.useEffect(() => {
    if (pendingSessionStart) {
      setPendingSessionStart(false);
      startMemoryInterview();
    }
  }, [pendingSessionStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle "Start Another" from saved screen
  const handleStartAnother = () => {
    resetMemoryInterview();
    setMcStep('welcome');
    setMcMood(null);
    setMcSessionTopic(null);
    setSelectedMoment(null);
    // mcSubject stays populated for continuity
  };

  const isInGuidedFlow = memoryTab === 'interview' && memoryStep === 'intro';

  return (
    <div>
      <div className="page-header">
        <h2> Memory Care</h2>
        <p>Tools and resources for families navigating memory loss, dementia, and Alzheimer's.</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px 20px', marginBottom: '16px', background: 'rgba(15,23,42,0.02)', border: '1px solid rgba(15,23,42,0.05)', borderRadius: '60px', maxWidth: '560px', margin: '0 auto 20px' }}>
        <span style={{ fontSize: '1.05rem', opacity: 0.6 }}>🤍</span>
        <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', fontStyle: 'italic' }}>This feature is always free. No premium required.</span>
      </div>

      {/* Sub-navigation — show when on intro step but NOT during guided interview flow steps */}
      {memoryStep === 'intro' && (mcStep === 'welcome' || memoryTab !== 'interview') && (
        <div className="memory-tab-bar" style={{ display: 'flex', gap: '2px', marginBottom: '24px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.22)', borderRadius: '8px', padding: '4px', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: '🏠 Overview' },
            { id: 'interview', label: ' Interview' },
            { id: 'voice', label: ' Voice Clips' },
            { id: 'resources', label: ' Resources' },
          ].map(t => (
            <button key={t.id} className="memory-tab-btn" onClick={() => { setMemoryTab(t.id); if (t.id === 'interview') setMcStep('welcome'); }}
              style={{ flex: 1, minWidth: '80px', padding: '12px 6px', background: memoryTab === t.id ? 'rgba(15,23,42,0.08)' : 'none', border: 'none', borderRadius: '6px', color: memoryTab === t.id ? '#0f172a' : '#7a6b5a', fontFamily: "'Lora', serif", fontSize: '1.05rem', letterSpacing: '0.5px', cursor: 'pointer', transition: 'all 0.2s', borderBottom: memoryTab === t.id ? '2px solid #0f172a' : '2px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ OVERVIEW TAB ═══ */}
      {memoryStep === 'intro' && memoryTab === 'overview' && (
        <div>
          {/* Hero card */}
          <div className="card" style={{ background: 'linear-gradient(135deg, rgba(140,80,180,0.05) 0%, rgba(15,23,42,0.03) 100%)', border: '1px solid rgba(140,80,180,0.15)', textAlign: 'center', padding: '40px 28px' }}>
            <div style={{ fontSize: '2.8rem', marginBottom: '16px' }}></div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#0f172a' }}>Their memory is fading. Their story doesn't have to.</h3>
            <p style={{ color: '#3c4653', fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 520, margin: '0 auto 28px' }}>
              Whether someone you love has been diagnosed or you're just noticing changes, Talebound gives you tools to preserve what matters — their voice, their stories, and the things that made them who they are.
            </p>
            <button className="btn btn-primary" onClick={() => setMemoryTab('interview')} style={{ fontSize: '1.05rem', padding: '16px 32px' }}>
               Start an Interview
            </button>
          </div>

          {/* Quick actions grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px', marginBottom: '24px' }}>
            {[
              { icon: '', title: 'Interview', desc: 'Sit with them and capture memories through guided questions', tab: 'interview' },
              { icon: '📓', title: 'Journal', desc: 'A private space for your daily thoughts and reflections', action: () => setActiveTab('journal') },
              { icon: '', title: 'Voice Clips', desc: 'Record their voice — even a few words are priceless', tab: 'voice' },
              { icon: '', title: 'Resources', desc: 'Practical guides for families navigating memory loss', tab: 'resources' },
            ].map(item => (
              <div key={item.tab || item.title} onClick={() => item.action ? item.action() : setMemoryTab(item.tab)}
                style={{ padding: '24px 20px', background: '#fff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: '14px', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.08)'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '12px' }}>{item.icon}</div>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>{item.title}</div>
                <div style={{ fontSize: '1rem', color: '#3c4653', lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>

          {/* Their Story So Far */}
          {(() => {
            const interviewStories = stories.filter(s => s.type === 'memory_interview');
            if (interviewStories.length === 0) return null;
            return (
              <div className="card" style={{ border: '1px solid rgba(140,80,180,0.15)', background: 'rgba(140,80,180,0.02)' }}>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '8px', color: '#0f172a' }}> Their Story So Far</h3>
                <p style={{ color: '#3c4653', fontSize: '1.05rem', marginBottom: '20px' }}>
                  You've captured {interviewStories.length} interview{interviewStories.length !== 1 ? 's' : ''}. Every session adds to the portrait of who they are.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {interviewStories.slice(0, 5).map(s => (
                    <div key={s.id} onClick={() => setViewingStory(s)}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#fff', border: '1px solid rgba(15,23,42,0.06)', borderRadius: '10px', cursor: 'pointer' }}>
                      <span style={{ fontSize: '1.1rem' }}></span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 500, color: '#0f172a' }}>{s.title}</div>
                        <div style={{ fontSize: '1rem', color: '#3c4653' }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                      </div>
                      <span style={{ color: '#3c4653', fontSize: '1.05rem' }}>Read ›</span>
                    </div>
                  ))}
                </div>
                {interviewStories.length > 5 && (
                  <button onClick={() => setActiveTab('stories')} style={{ background: 'none', border: 'none', color: '#7a6b5a', fontFamily: "'Lora', serif", fontSize: '1rem', cursor: 'pointer', marginTop: '12px', padding: 0 }}>
                    View all {interviewStories.length} interviews →
                  </button>
                )}
              </div>
            );
          })()}

          {/* Journal link */}
          {journalEntries.length > 0 && (
            <div className="card" style={{ background: 'rgba(15,23,42,0.02)' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '8px', color: '#0f172a' }}>📓 Your Journal</h3>
              <p style={{ color: '#3c4653', fontSize: '1rem', marginBottom: '12px' }}>{journalEntries.length} entr{journalEntries.length !== 1 ? 'ies' : 'y'}</p>
              <button onClick={() => setActiveTab('journal')} style={{ background: 'none', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653' }}>
                Open Journal →
              </button>
            </div>
          )}

          {/* Crosslink to My Book */}
          <div style={{ textAlign: 'center', paddingTop: '12px' }}>
            <button onClick={() => setActiveTab('st-book')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>
               Want to tell your own story? Go to My Book →
            </button>
          </div>
        </div>
      )}

      {/* ═══ INTERVIEW TAB — NEW GUIDED FLOW ═══ */}
      {isInGuidedFlow && (
        <div>
          <ProgressDots mcStep={mcStep} />

          {/* ── STEP 1: WELCOME ── */}
          {mcStep === 'welcome' && (
            <div style={{ ...cardStyle, ...cardStyleMobile }}>
              {memoryCareSubjects.length > 0 ? (
                <>
                  <div style={eyebrowStyle}>Welcome back</div>
                  <h3 style={headingStyle}>Ready to sit with {displaySubject?.name || 'them'} again?</h3>
                  <p style={subStyle}>You have {displaySubject?.capturedThemes?.length || 0} chapters in {displaySubject?.name || 'their'}'s book so far.</p>

                  {/* Book progress strip */}
                  {displaySubject && (
                    <div style={{ background: '#f5efd8', border: '1px solid #e8d5a3', borderRadius: 14, padding: 16, marginBottom: 24 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: '1.1rem' }}></span>
                        <span style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>
                          {displaySubject.name}'s Book — {displaySubject.sessionCount || 0} session{(displaySubject.sessionCount || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {displaySubject.lastSessionTitle && (
                        <div style={{ fontSize: '0.85rem', color: '#3c4653', marginBottom: 10 }}>
                          {displaySubject.lastSessionTitle} · {relativeDate(displaySubject.lastSessionDate)}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        {Array.from({ length: 10 }).map((_, i) => (
                          <div key={i} style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: i < (displaySubject.capturedThemes?.length || 0) ? '#7c2d12' : '#fff',
                            border: `1.5px solid ${i < (displaySubject.capturedThemes?.length || 0) ? '#7c2d12' : 'rgba(201,168,76,0.4)'}`,
                          }} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 style={headingStyle}>Let's capture a story</h3>
                  <p style={subStyle}>Each session becomes a chapter in a keepsake book. Start by telling us who you're sitting with.</p>
                </>
              )}

              {/* Name inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 480 ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '0.85rem', letterSpacing: 2, color: '#3c4653', textTransform: 'uppercase', marginBottom: 6 }}>Their Name</label>
                  <input className="inp" value={wcName} onChange={e => setWcName(e.target.value)} placeholder="e.g. Mom, Grandma Rose..." />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '0.85rem', letterSpacing: 2, color: '#3c4653', textTransform: 'uppercase', marginBottom: 6 }}>You are (optional)</label>
                  <input className="inp" value={wcCarerName} onChange={e => setWcCarerName(e.target.value)} placeholder="e.g. Sarah, their daughter..." />
                </div>
              </div>

              {/* Memory Loss Mode toggle */}
              <div
                onClick={() => setMemoryDementiaMode(!memoryDementiaMode)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: memoryDementiaMode ? '#f3f0f7' : 'rgba(15,23,42,0.02)', border: memoryDementiaMode ? '1.5px solid #d4cce0' : '1.5px solid rgba(15,23,42,0.08)', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', marginBottom: 24 }}>
                <div style={{ width: 44, height: 24, borderRadius: 12, background: memoryDementiaMode ? '#9b8faa' : 'rgba(15,23,42,0.15)', position: 'relative', transition: 'all 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: memoryDementiaMode ? 23 : 3, transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>Memory Loss Mode</div>
                  <div style={{ fontSize: '0.85rem', color: '#3c4653', marginTop: 2 }}>For someone with dementia or Alzheimer's — gentler questions, more patience</div>
                </div>
              </div>

              <button style={{ ...lavenderBtnStyle, opacity: wcName.trim() ? 1 : 0.5 }}
                onClick={handleWelcomeContinue} disabled={!wcName.trim()}>
                Continue →
              </button>

              {memoryCareSubjects.length > 0 && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button onClick={handleNewSubject} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', color: '#9b8faa', fontFamily: "'Lora', serif" }}>
                    This is someone new
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: MOOD ── */}
          {mcStep === 'mood' && (
            <div style={{ ...cardStyle, ...cardStyleMobile }}>
              <div style={eyebrowStyle}>Before we begin</div>
              <h3 style={headingStyle}>How is {mcSubject?.name} doing today?</h3>
              <p style={subStyle}>This shapes the questions we ask.</p>

              <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 480 ? '1fr' : '1fr 1fr 1fr', gap: 10, marginBottom: 24 }}>
                {[
                  { id: 'good', emoji: '☀️', label: 'Good day', desc: 'Alert, talkative' },
                  { id: 'mixed', emoji: '🌥️', label: 'Mixed', desc: 'Some clarity' },
                  { id: 'hard', emoji: '🌙', label: 'Hard day', desc: 'Tired, confused' },
                ].map(m => (
                  <div key={m.id} onClick={() => setMcMood(m.id)}
                    style={{
                      padding: 18, textAlign: 'center', borderRadius: 14, cursor: 'pointer',
                      border: mcMood === m.id ? '1.5px solid #9b8faa' : '1.5px solid rgba(15,23,42,0.1)',
                      background: mcMood === m.id ? '#f3f0f7' : '#fff',
                      boxShadow: mcMood === m.id ? '0 2px 8px rgba(155,143,170,0.15)' : 'none',
                      transition: 'all 0.2s',
                    }}>
                    <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>{m.emoji}</div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: '0.85rem', color: '#3c4653' }}>{m.desc}</div>
                  </div>
                ))}
              </div>

              <button style={{ ...lavenderBtnStyle, opacity: mcMood ? 1 : 0.5 }}
                onClick={handleMoodContinue} disabled={!mcMood}>
                This feels right →
              </button>
              <div style={{ textAlign: 'center' }}>
                <button onClick={() => setMcStep('welcome')} style={backLinkStyle}>← Back</button>
              </div>
            </div>
          )}

          {/* ── STEP 3: SUGGEST ── */}
          {mcStep === 'suggest' && (
            <div style={{ ...cardStyle, ...cardStyleMobile }}>
              <div style={eyebrowStyle}>Today's session</div>
              <h3 style={headingStyle}>We have a suggestion for today</h3>
              <p style={subStyle}>Based on what {mcSubject?.name} {mcSubject?.sessionCount > 0 ? 'has shared' : 'might enjoy'}, and what hasn't come up yet.</p>

              {/* AI Suggestion card */}
              <div
                onClick={() => {
                  if (mcSuggestion) {
                    setMcSessionTopic(mcSuggestion);
                    setSelectedMoment(null);
                  } else if (!mcSuggestionLoading) {
                    // Fallback
                    setMcSessionTopic({ theme: 'free', title: `Follow ${mcSubject?.name || 'their'} lead today`, reason: `Sometimes the best session starts with whatever is already on ${mcSubject?.name || 'their'} mind.`, openingQuestion: 'What\'s been on your mind lately? Tell me anything.' });
                    setSelectedMoment(null);
                  }
                }}
                style={{
                  background: '#f3f0f7', border: `1.5px solid ${mcSessionTopic && !selectedMoment && mcSessionTopic !== null ? '#9b8faa' : '#d4cce0'}`,
                  borderRadius: 16, padding: 0, marginBottom: 20, cursor: mcSuggestionLoading ? 'default' : 'pointer',
                  boxShadow: mcSessionTopic && !selectedMoment ? '0 4px 12px rgba(155,143,170,0.2)' : 'none',
                  transform: mcSessionTopic && !selectedMoment ? 'translateY(-2px)' : 'none',
                  transition: 'all 0.2s', overflow: 'hidden',
                }}>
                {/* Top accent bar */}
                <div style={{ height: 3, background: 'linear-gradient(90deg, #9b8faa, #d4cce0)' }} />
                <div style={{ padding: 24 }}>
                  {mcSuggestionLoading ? (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                      <div style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', fontStyle: 'italic', color: '#9b8faa' }}>Finding the right topic...</div>
                    </div>
                  ) : mcSuggestion ? (
                    <>
                      <div style={{ fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase', color: '#9b8faa', marginBottom: 8 }}>✦ Suggested for today</div>
                      <div style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{mcSuggestion.title}</div>
                      <div style={{ fontSize: '0.85rem', color: '#3c4653', lineHeight: 1.55, marginBottom: 12 }}>{mcSuggestion.reason}</div>
                      <div style={{ background: '#fff', borderRadius: 8, borderLeft: '2px solid #9b8faa', padding: '10px 14px' }}>
                        <div style={{ fontFamily: "'Lora', serif", fontSize: '0.85rem', fontStyle: 'italic', color: '#9b8faa', lineHeight: 1.5 }}>"{mcSuggestion.openingQuestion}"</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase', color: '#9b8faa', marginBottom: 8 }}>✦ Suggested for today</div>
                      <div style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Follow {mcSubject?.name || 'their'} lead today</div>
                      <div style={{ fontSize: '0.85rem', color: '#3c4653', lineHeight: 1.55, marginBottom: 12 }}>Sometimes the best session starts with whatever is already on {mcSubject?.name || 'their'} mind.</div>
                      <div style={{ background: '#fff', borderRadius: 8, borderLeft: '2px solid #9b8faa', padding: '10px 14px' }}>
                        <div style={{ fontFamily: "'Lora', serif", fontSize: '0.85rem', fontStyle: 'italic', color: '#9b8faa', lineHeight: 1.5 }}>"What's been on your mind lately? Tell me anything."</div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(15,23,42,0.08)' }} />
                <span style={{ fontSize: '0.8rem', color: '#3c4653', whiteSpace: 'nowrap' }}>or follow what's already happening</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(15,23,42,0.08)' }} />
              </div>

              {/* Moment grid */}
              <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 480 ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 24 }}>
                {MOMENT_ITEMS.map((item, i) => (
                  <div key={i} onClick={() => {
                    setSelectedMoment(i);
                    setMcSessionTopic({ triggerId: item.triggerId, title: item.label, reason: '', openingQuestion: '' });
                  }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                      border: selectedMoment === i ? '1.5px solid #9b8faa' : '1.5px solid rgba(15,23,42,0.1)',
                      borderRadius: 12, cursor: 'pointer',
                      background: selectedMoment === i ? '#f3f0f7' : '#fff',
                      transition: 'all 0.15s',
                    }}>
                    <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                    <span style={{ fontSize: '0.9rem', color: '#0f172a' }}>{item.label}</span>
                  </div>
                ))}
              </div>

              <button style={{ ...lavenderBtnStyle, opacity: mcSessionTopic ? 1 : 0.5 }}
                onClick={() => { if (mcSessionTopic) setMcStep('breath'); }}
                disabled={!mcSessionTopic}>
                Start with this →
              </button>
              <div style={{ textAlign: 'center' }}>
                <button onClick={() => { setMcStep('mood'); setMcSessionTopic(null); setSelectedMoment(null); }} style={backLinkStyle}>← Back</button>
              </div>
            </div>
          )}

          {/* ── STEP 4: BREATH ── */}
          {mcStep === 'breath' && (
            <div style={{ ...cardStyle, ...cardStyleMobile, textAlign: 'center', padding: '48px 32px' }}>
              <div style={{ fontSize: '3.2rem', marginBottom: 16 }}></div>
              <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.4rem', fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>You're about to capture</h3>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', fontStyle: 'italic', color: '#3c4653', marginBottom: 24 }}>{mcSessionTopic?.title || 'a new memory'}</div>

              <div style={{ background: '#faf8f5', border: '1px solid #e0d5c5', borderRadius: 12, padding: 20, marginBottom: 28, textAlign: 'left' }}>
                <p style={{ fontSize: '0.9rem', color: '#3c4653', lineHeight: 1.7, margin: 0 }}>
                  Take a breath. There's no rush.
                  <br /><br />
                  The questions come one at a time. Type what {mcSubject?.name} says — exact words, even if {mcSubject?.name} trails off. Your observations matter too — "{mcSubject?.name} smiled" or "{mcSubject?.name} got quiet" are part of the story.
                  <br /><br />
                  When you're ready, this becomes Chapter {(mcSubject?.sessionCount || 0) + 1} in {mcSubject?.name}'s book.
                </p>
              </div>

              <button style={goldBtnStyle} onClick={handleBeginSession}>
                 Begin Session
              </button>
              <div>
                <button onClick={() => { setMcStep('suggest'); setMcSessionTopic(null); setSelectedMoment(null); }} style={{ ...backLinkStyle, marginTop: 16 }}>← Choose a different topic</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ RESOURCES TAB ═══ */}
      {memoryStep === 'intro' && memoryTab === 'resources' && (
        <div>
          <div className="card" style={{ background: 'linear-gradient(135deg, rgba(140,80,180,0.06) 0%, rgba(100,60,140,0.04) 100%)', border: '1px solid rgba(140,80,180,0.2)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}></div>
            <h3 style={{ marginBottom: '12px', fontSize: '1.4rem' }}>For Families Navigating Memory Loss</h3>
            <p style={{ color: '#3c4653', fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '0' }}>
              If someone you love is living with dementia or Alzheimer's, you're carrying something heavy. This section is here to help — not with medical advice, but with practical guidance and the reminder that their story still matters, even now.
            </p>
          </div>

          {[
            { icon: '⏰', title: 'Start Now — Don\'t Wait', body: 'The most common regret families share is waiting too long. Even if your loved one\'s memory is already fading, they likely still have moments of clarity, emotional memories, and feelings they can express. A conversation today — even a short one — is worth more than a perfect one that never happens. Use the interview tool above. Keep sessions short (10-15 minutes). Follow their energy, not a script.' },
            { icon: '💡', title: 'Tips for Interviewing Someone with Memory Loss', body: 'Ask about the distant past — childhood memories often stay longest. Use photos, music, or familiar objects to trigger memories. Don\'t correct them if details are wrong — the feeling matters more than the facts. Keep questions simple and open-ended. If they get confused or frustrated, gently change the subject or take a break. Repeat their words back to them warmly ("That sounds like a wonderful day"). Let silence happen — give them time to find the words.' },
            { icon: '', title: 'Legal & Financial Steps Most Families Miss', body: 'If your loved one can still make decisions, act now on these: Power of Attorney (financial and healthcare) should be established while they can still legally consent. Review beneficiary designations on all accounts. Consider a living trust to avoid probate. Look into Medicaid planning if long-term care may be needed — there are asset protection strategies, but they require advance planning. Document their wishes for care — do they want to stay home? What matters to them about daily life?' },
            { icon: '🏥', title: 'Navigating Care Decisions', body: 'Every family faces these questions eventually: When is it no longer safe for them to live alone? How do you choose between home care, assisted living, and memory care facilities? What\'s the difference between Medicare and Medicaid coverage for dementia care? These decisions are overwhelming. Talk to their doctor, consult an elder care attorney, and reach out to your local Alzheimer\'s Association chapter — they offer free care consultations.' },
            { icon: '🫂', title: 'Taking Care of Yourself', body: 'Caregiver burnout is real and it\'s not a sign of weakness. You cannot pour from an empty cup. Look into respite care — even a few hours a week. Join a caregiver support group (the Alzheimer\'s Association runs them in every state, including virtual options). Ask for help — specifically. "Can you sit with Mom on Tuesday?" works better than "Let me know if you can help." It\'s okay to grieve someone who is still here. That grief is valid.' },
            { icon: '📞', title: 'Resources & Helplines', body: 'Alzheimer\'s Association 24/7 Helpline: 1-800-272-3900 (free, confidential, available day and night). Eldercare Locator: 1-800-677-1116 (connects you to local aging services). Family Caregiver Alliance: caregiver.org. National Institute on Aging: nia.nih.gov/alzheimers. These organizations can help with everything from understanding the diagnosis to finding local support groups, legal resources, and care facilities.' },
          ].map(item => (
            <div key={item.title} className="card" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '1.6rem', flexShrink: 0, marginTop: '2px' }}>{item.icon}</div>
              <div>
                <h3 style={{ marginBottom: '12px', fontSize: '1.05rem' }}>{item.title}</h3>
                <p style={{ color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.85, fontFamily: "'Lora', serif" }}>{item.body}</p>
              </div>
            </div>
          ))}

          <div className="card" style={{ textAlign: 'center', background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.22)' }}>
            <p style={{ fontFamily: "'Lora', serif", fontSize: '1.3rem', fontStyle: 'italic', color: '#0f172a', lineHeight: 1.7, marginBottom: '16px' }}>
              Their memory may be fading, but their story doesn't have to.
            </p>
            <button className="btn btn-primary" onClick={() => setMemoryTab('interview')}>
               Start an Interview
            </button>
          </div>
        </div>
      )}

      {/* ═══ VOICE CLIPS TAB ═══ */}
      {memoryStep === 'intro' && memoryTab === 'voice' && (
        <div>
          <div className="card" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.03) 0%, rgba(180,80,80,0.04) 100%)', border: '1px solid rgba(15,23,42,0.12)', textAlign: 'center', padding: '40px 28px', marginBottom: '20px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}></div>
            <h3 style={{ fontSize: '1.4rem', marginBottom: '12px', color: '#0f172a' }}>Capture Their Voice</h3>
            <p style={{ color: '#3c4653', fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 500, margin: '0 auto 8px' }}>
              Someday, hearing their voice will mean everything. Even a few words — their laugh, the way they say your name, a song they hum — are worth preserving.
            </p>
          </div>

          {/* Voice recording prompts */}
          <div className="card">
            <h4 style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '16px' }}>Things to Record</h4>
            <p style={{ color: '#3c4653', fontSize: '1.05rem', marginBottom: '20px', lineHeight: 1.7 }}>
              Hit record on your phone and try any of these. Don't worry about perfection — the raw, real sound of their voice is what matters.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { prompt: 'Ask them to say each grandchild\'s name', why: 'Hearing their name in that voice will be a treasure' },
                { prompt: 'Ask them to sing a song they love', why: 'Music memories are often the last to go' },
                { prompt: '"Tell me about when you were little"', why: 'Childhood memories persist longest with dementia' },
                { prompt: 'Ask them to say "I love you" to someone specific', why: 'The simplest words carry the most weight' },
                { prompt: 'Record them laughing', why: 'Sometimes the laugh is what you miss most' },
                { prompt: '"What\'s your favorite meal?"', why: 'Food memories connect to deep emotion' },
                { prompt: 'Ask them to tell a joke they always tell', why: 'The one everyone\'s heard a hundred times — that\'s the one' },
                { prompt: '"What do you want people to remember about you?"', why: 'If they can answer this, it\'s gold' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px 18px', background: i % 2 === 0 ? 'rgba(15,23,42,0.02)' : '#fff', borderRadius: '10px' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '2px' }}></span>
                  <div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 500, color: '#0f172a', marginBottom: '4px' }}>{item.prompt}</div>
                    <div style={{ fontSize: '1.05rem', color: '#3c4653', fontStyle: 'italic' }}>{item.why}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tip card */}
          <div className="card" style={{ background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.08)' }}>
            <h4 style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', marginBottom: '10px' }}>💡 Recording Tips</h4>
            <div style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.8 }}>
              <p style={{ marginBottom: '8px' }}>Use the Voice Memos app on your phone or the recording feature in Talebound's interview tool. A quiet room helps, but don't stress about background noise — real life is part of the memory.</p>
              <p style={{ marginBottom: '8px' }}>Keep recordings short — 30 seconds to 2 minutes is perfect. You can always record more later.</p>
              <p style={{ margin: 0 }}>If they're having a good day, record as much as you can. Good days are gifts.</p>
            </div>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', color: '#0f172a', fontStyle: 'italic', marginBottom: '16px' }}>
              Ready to capture their story with their voice?
            </p>
            <button className="btn btn-primary" onClick={() => setMemoryTab('interview')}>
               Start a Recorded Interview
            </button>
          </div>
        </div>
      )}

      {/* ── INTERVIEW SESSION (existing chat UI with new header) ── */}
      {memoryStep === 'interview' && (
        <div>
          {/* Session header bar */}
          {mcStep === 'session' && mcSubject && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', background: '#fff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 14, marginBottom: 16 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#f3f0f7', border: '1.5px solid #d4cce0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '17px', fontWeight: 600, color: '#0f172a' }}>{mcSubject.name}</div>
                <div style={{ fontSize: '14px', color: '#3c4653' }}>{mcSessionTopic?.title || 'Interview'}</div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'rgba(122,173,140,0.1)', border: '1px solid rgba(122,173,140,0.2)', borderRadius: 20, fontSize: '13px', color: '#5a8a5a' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#7aad8c', display: 'inline-block' }} />
                In session
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.5rem' }}>{memoryTopic?.icon || ''}</span>
              <div>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase' }}>{memoryTopic?.label || 'Interview'}</div>
                <div style={{ color: '#0f172a', fontSize: '1.05rem' }}>Interviewing {memorySubjectName}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {(() => {
                const userAnswers = memoryMessages.filter(m => m.role === 'user' && !m.content.startsWith('Please begin') && !m.content.startsWith('Sitting with') && !m.content.startsWith('We\'re sitting with')).length;
                const needMore = Math.max(0, 3 - userAnswers);
                return (
                  <button className="btn btn-primary" style={{ fontSize: '15px', padding: '10px 20px', opacity: needMore > 0 ? 0.4 : 1 }} onClick={generateMemoryStory} disabled={needMore > 0 || memoryLoading}>
                    {memoryLoading ? ' Writing...' : needMore > 0 ? ` Save Story (answer ${needMore} more)` : ' We\'re done for today'}
                  </button>
                );
              })()}
              <button className="btn btn-ghost" style={{ fontSize: '15px', padding: '10px 16px', color: 'rgba(180,80,80,0.7)' }}
                onClick={() => {
                  // UX_BACKLOG H-05: "End" silently discarded the whole session. Confirm before
                  // throwing away captured answers, and point the caregiver at the save action.
                  const answers = memoryMessages.filter(m => m.role === 'user' && !m.content.startsWith('Please begin') && !m.content.startsWith('Sitting with') && !m.content.startsWith('We\'re sitting with')).length;
                  if (answers > 0 && !window.confirm(`End this session with ${memorySubjectName}? The ${answers} answer${answers !== 1 ? 's' : ''} you've captured won't be saved. To keep them, tap "We're done for today" instead.`)) return;
                  handleStartAnother();
                }}>
                End session
              </button>
            </div>
          </div>

          {/* Chat messages */}
          <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ maxHeight: '420px', overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {memoryMessages.length <= 1 && (
                <div style={{
                  margin: '0 0 16px',
                  padding: '12px 16px',
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: 8,
                  fontSize: '0.9rem',
                  color: '#3c4653',
                  fontStyle: 'italic',
                  lineHeight: 1.6
                }}>
                  💡 Tip: You can also type what you notice — "he smiled," "she got quiet," "she reached for my hand." These moments become part of the story.
                </div>
              )}
              {memoryMessages.filter(m => !m.content.startsWith('Please begin') && !m.content.startsWith('Sitting with') && !m.content.startsWith('We\'re sitting with')).map((msg, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: msg.role === 'assistant' ? 'row' : 'row-reverse', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', background: msg.role === 'assistant' ? 'rgba(15,23,42,0.08)' : 'rgba(140,80,180,0.15)', border: msg.role === 'assistant' ? '1px solid rgba(15,23,42,0.25)' : '1px solid rgba(140,80,180,0.3)' }}>
                    {msg.role === 'assistant' ? '' : ''}
                  </div>
                  <div style={{ maxWidth: '75%', padding: '14px 18px', borderRadius: '12px', background: msg.role === 'assistant' ? 'rgba(15,23,42,0.04)' : 'rgba(140,80,180,0.08)', border: msg.role === 'assistant' ? '1px solid rgba(15,23,42,0.08)' : '1px solid rgba(140,80,180,0.15)', color: '#0f172a', fontSize: '16px', lineHeight: 1.75, fontStyle: msg.role === 'assistant' ? 'normal' : 'italic' }}>
                    {msg.content.replace(/\*\*/g, '').replace(/^#+\s*/gm, '').replace(/\*/g, '')}
                  </div>
                </div>
              ))}
              {memoryLoading && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(15,23,42,0.22)', border: '1px solid rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></div>
                  <div style={{ color: '#8a6d3a', fontStyle: 'italic', fontSize: '1rem' }}>Listening...</div>
                </div>
              )}
              <div ref={chatBottomRef} />
            </div>
            <div style={{ borderTop: '1px solid rgba(15,23,42,0.07)', padding: '16px 24px' }}>
              {/* Audio/Video playback if recorded */}
              {interviewAudioUrl && !interviewRecording && (
                <div style={{ marginBottom: '12px', padding: '10px 14px', background: 'rgba(140,80,180,0.06)', border: '1px solid rgba(140,80,180,0.2)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span>{interviewVideoBlob ? '' : '🎙'}</span>
                    <audio controls src={interviewAudioUrl} style={{ flex: 1, height: '32px', minWidth: '180px' }} />
                    <button className="btn btn-secondary" style={{ padding: '5px 12px', fontSize: '15px' }} onClick={async () => {
                      const text = await transcribeAudio(interviewAudioBlob);
                      if (text) { setMemoryInput(prev => prev ? prev + ' ' + text : text); showToast('Transcription added!', 'success'); }
                    }} disabled={transcribing}>
                      {transcribing ? '✦ Transcribing...' : '✦ Transcribe'}
                    </button>
                    <button onClick={() => { setInterviewAudioBlob(null); setInterviewAudioUrl(''); setInterviewVideoBlob(null); setInterviewVideoUrl(''); }} style={{ background: 'none', border: 'none', color: '#0f172a', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
                  </div>
                </div>
              )}
              {/* Persistent video element — always in DOM when video mode is on */}
              {interviewVideoMode && (
                <video ref={interviewLiveVideoRef} muted playsInline
                  style={{ width: '100%', maxWidth: 320, borderRadius: 12, marginBottom: 8, background: '#1a1612', display: interviewRecording ? 'block' : 'none', border: '2px solid rgba(220,80,60,0.4)' }} />
              )}
              {interviewRecording && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fc8181', fontSize: '1rem' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fc8181', animation: 'pulse 1s infinite' }} />
                    Recording{interviewVideoMode ? ' video' : ''} — click stop when finished, then transcribe
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <textarea
                  value={memoryInput}
                  onChange={e => setMemoryInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMemoryMessage(); } }}
                  placeholder={`Type what ${mcSubject?.name || memorySubjectName} says — or note what you observe (smiled, got quiet, reached for your hand...)`}
                  rows={2}
                  style={{ flex: 1, background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(15,23,42,0.6)', borderRadius: '8px', padding: '12px 16px', color: '#0f172a', fontFamily: "'Lora', serif", fontSize: '16px', resize: 'none', outline: 'none', lineHeight: 1.6 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignSelf: 'flex-end' }}>
                  {/* Audio/Video mode toggle */}
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => setInterviewVideoMode(false)}
                      title="Voice recording"
                      style={{ padding: '6px 12px', fontSize: '0.85rem', fontFamily: "'Lora', serif", fontWeight: 600, background: !interviewVideoMode ? 'rgba(140,80,180,0.25)' : 'rgba(140,80,180,0.04)', color: !interviewVideoMode ? '#7a4aad' : '#3c4653', border: '1px solid rgba(140,80,180,0.2)', borderRadius: '16px 0 0 16px', cursor: 'pointer', transition: 'all 0.2s' }}>
                      
                    </button>
                    <button onClick={() => setInterviewVideoMode(true)}
                      title="Video recording"
                      style={{ padding: '6px 12px', fontSize: '0.85rem', fontFamily: "'Lora', serif", fontWeight: 600, background: interviewVideoMode ? 'rgba(140,80,180,0.25)' : 'rgba(140,80,180,0.04)', color: interviewVideoMode ? '#7a4aad' : '#3c4653', border: '1px solid rgba(140,80,180,0.2)', borderRadius: '0 16px 16px 0', cursor: 'pointer', transition: 'all 0.2s' }}>
                      
                    </button>
                  </div>
                  <button onClick={interviewRecording ? stopInterviewRecording : startInterviewRecording}
                    title={interviewRecording ? 'Stop recording' : `Record answer with ${interviewVideoMode ? 'video' : 'voice'}`}
                    style={{ width: '42px', height: '42px', borderRadius: '50%', border: interviewRecording ? '2px solid #fc8181' : '1px solid rgba(140,80,180,0.3)', background: interviewRecording ? 'rgba(252,129,129,0.15)' : 'rgba(140,80,180,0.08)', color: interviewRecording ? '#fc8181' : '#c4a0ff', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', animation: interviewRecording ? 'pulse 1s infinite' : 'none' }}>
                    {interviewRecording ? '⏹' : '⏺'}
                  </button>
                  <button className="btn btn-primary" onClick={sendMemoryMessage} disabled={memoryLoading || !memoryInput.trim()} style={{ padding: '10px 16px', fontSize: '15px' }}>
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>

          {memoryMessages.filter(m => m.role === 'user' && !m.content.startsWith('Please begin') && !m.content.startsWith('Sitting with') && !m.content.startsWith('We\'re sitting with')).length >= 3 && (
            <div style={{ marginTop: '16px', padding: '16px 20px', background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(15,23,42,0.6)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <p style={{ color: '#0f172a', fontSize: '1rem', fontStyle: 'italic' }}>
                Ready to turn this conversation into a story {memorySubjectName}'s family can keep?
              </p>
              <button className="btn btn-primary" onClick={generateMemoryStory} disabled={memoryLoading}>
                 We're done for today
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── REVIEW ── */}
      {memoryStep === 'review' && (
        <div>
          <div className="card" style={{ background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.6)', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '1.5rem' }}></span>
              <h3>{memorySubjectName}'s Story</h3>
            </div>
            <p style={{ color: '#3c4653', fontSize: '1.05rem', fontStyle: 'italic', marginBottom: '20px' }}>
              Review and edit the story below before saving. Every word is based on what {memorySubjectName} shared with you.
            </p>
            <div>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '8px' }}>Story Title</label>
              <input className="inp" value={memoryTitle} onChange={e => setMemoryTitle(e.target.value)} style={{ marginBottom: '16px' }} />
            </div>
            <textarea
              value={memoryStory}
              onChange={e => setMemoryStory(e.target.value)}
              rows={14}
              style={{ width: '100%', background: '#ffffff', border: '1px solid rgba(15,23,42,0.22)', borderRadius: '8px', padding: '16px', color: '#0f172a', fontFamily: "'Lora', serif", fontSize: '1.1rem', lineHeight: 1.85, resize: 'vertical', outline: 'none' }}
            />

            {/* Photo upload */}
            <div style={{ marginTop: 16, padding: '16px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)' }}>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: 10 }}> Add a photo <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>(optional — you can always add one later)</span></label>
              {memoryPhotoPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={memoryPhotoPreview} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)' }} />
                  <div>
                    <p style={{ fontSize: '1rem', color: '#0f172a', marginBottom: 4 }}>Photo attached ✓</p>
                    <button onClick={() => { setMemoryPhotoFile(null); setMemoryPhotoPreview(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#c0392b', padding: 0, fontFamily: "'Lora', serif" }}>Remove</button>
                  </div>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 20px', background: 'rgba(15,23,42,0.06)', borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
                   Choose photo
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
                    if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
                    try {
                      const normalizedFile = await normalizeImageForBook(file);
                      const preview = URL.createObjectURL(normalizedFile);
                      setMemoryPhotoFile(normalizedFile);
                      setMemoryPhotoPreview(preview);
                    } catch (err) { showToast('Could not load photo.', 'error'); }
                  }} />
                </label>
              )}
            </div>
          </div>

          {/* Book selector — only if 2+ books */}
          {books.length > 1 && (() => {
            const sorted = [...books].sort((a, b) => (a.bookType === 'PERSONAL' ? 0 : 1) - (b.bookType === 'PERSONAL' ? 0 : 1));
            const selectedId = mcTargetBookId || sorted.find(b => b.bookType === 'PERSONAL')?.id || sorted[0]?.id;
            return (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '0.82rem', letterSpacing: 3, textTransform: 'uppercase', color: '#3c4653', marginBottom: 10 }}>Save to</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sorted.map(book => {
                    const isSelected = book.id === selectedId;
                    return (
                      <div key={book.id} onClick={() => setMcTargetBookId(book.id)} style={{
                        padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                        border: isSelected ? '1.5px solid #7c2d12' : '1.5px solid rgba(15,23,42,0.1)',
                        background: isSelected ? '#f5efd8' : '#faf8f5',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        transition: 'all 0.15s',
                      }}>
                        <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', fontWeight: isSelected ? 600 : 400 }}>{book.title || 'Untitled Book'}</span>
                        <span style={{
                          fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 6,
                          background: book.bookType === 'PERSONAL' ? 'rgba(15,23,42,0.06)' : 'rgba(140,80,180,0.08)',
                          color: book.bookType === 'PERSONAL' ? '#3c4653' : '#8c50b4',
                          border: book.bookType === 'PERSONAL' ? '1px solid rgba(15,23,42,0.1)' : '1px solid rgba(140,80,180,0.15)',
                        }}>{bookTypeChipLabel(book.bookType)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <button
              onClick={handleShapeIntoChapter}
              disabled={mcShaping}
              style={{
                padding: '12px 28px',
                background: mcShaping ? 'rgba(15,23,42,0.05)' : '#fbf8f1',
                border: '1px solid rgba(15,23,42,0.15)',
                borderRadius: 8,
                fontFamily: "'Lora', serif",
                fontSize: '0.95rem',
                color: '#0f172a',
                cursor: mcShaping ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {mcShaping ? 'Shaping...' : ' Shape into a chapter'}
            </button>
            <p style={{ marginTop: 8, fontSize: '0.85rem', color: '#3c4653', fontStyle: 'italic' }}>
              Their exact words, shaped for the page. Nothing invented, nothing changed.
            </p>
            {mcOriginalTranscript && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button onClick={handleRestoreTranscript} style={{ background: 'none', border: 'none', color: '#3c4653', fontFamily: "'Lora', serif", fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline', fontStyle: 'italic' }}>
                  ↩ Restore original transcript
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={() => setMemoryStep('interview')} style={{ flex: 1 }}>← Continue Interview</button>
            <button className="btn btn-ghost" onClick={() => window.print()} style={{ flex: 1 }}> Print</button>
            <button className="btn btn-primary" onClick={saveMemoryInterview} disabled={savingMemory} style={{ flex: 2 }}>
              {savingMemory ? 'Saving...' : (interviewAudioBlob || interviewVideoBlob) ? ` Save Story + ${interviewVideoBlob ? 'Video' : 'Recording'}` : ' Save to My Stories'}
            </button>
          </div>
        </div>
      )}

      {/* ── SAVED ── */}
      {memoryStep === 'saved' && (
        <div className="card" style={{ textAlign: 'center', maxWidth: '520px', margin: '0 auto', padding: '48px 40px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}></div>
          <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#0f172a' }}>Saved forever.</h3>
          <p style={{ color: '#3c4653', fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '32px' }}>
            <strong style={{ color: '#0f172a' }}>{memoryTitle}</strong> has been saved to your stories and added to your book. A gift that will be treasured for generations.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={() => { setActiveTab('st-book'); setStBookStep(0); }}>View My Book</button>
            <button className="btn btn-secondary" onClick={handleStartAnother}>Start Another Session</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryInterviewPage;
